import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// ── NSV ACADEMY CMS ───────────────────────────────────────────
// Admin-only content management for the NSV Notary Academy
// Nancy uploads curriculum here — zero deployment needed!!
//
// Structure: Tracks → Modules → Lessons → Quiz Questions
//            Tracks → Final Exam (with Sections → Questions)

// ── EMPTY TEMPLATES ───────────────────────────────────────────
const EMPTY_TRACK = {
  title:             '',
  subtitle:          '',
  description:       '',
  icon:              '🎓',
  color:             '#5E8A72',
  published:         false,
  sort_order:        0,
  final_exam_title:  '',
  final_exam_intro:  '',
}

const EMPTY_MODULE = {
  title:      '',
  icon:       '📋',
  sort_order: 0,
  published:  false,
}

const EMPTY_LESSON = {
  title:      '',
  content:    '',
  sort_order: 0,
  published:  false,
}

const EMPTY_QUESTION = {
  question:       '',
  option_a:       '',
  option_b:       '',
  option_c:       '',
  option_d:       '',
  correct_answer: 'a',
}

const EMPTY_SECTION = {
  title:       '',
  description: '',
  sort_order:  0,
}

// ── ICON OPTIONS ──────────────────────────────────────────────
const ICONS = ['🎓','📋','✅','✍️','📤','💰','⭐','🔄','🤝','🔒','⚖️','💻','🏆','📚','🛡️','🎯','🌟','💡','🔑','📝']
const COLORS = [
  { label:'Green',  value:'#5E8A72' },
  { label:'Blue',   value:'#185FA5' },
  { label:'Purple', value:'#6A3A9A' },
  { label:'Gold',   value:'#41427a' },
  { label:'Navy',   value:'#151715' },
  { label:'Teal',   value:'#2D7A6A' },
]

// ── SHARED STYLES ─────────────────────────────────────────────
const INPUT_STYLE = {
  width:        '100%',
  padding:      '10px 14px',
  border:       '1.5px solid #D7DADE',
  borderRadius: 10,
  fontSize:     13,
  fontFamily:   'inherit',
  color:        '#151715',
  background:   '#F7F8F9',
  boxSizing:    'border-box',
}

const TEXTAREA_STYLE = {
  ...INPUT_STYLE,
  resize:     'vertical',
  lineHeight: 1.7,
  minHeight:  120,
}

const BTN_PRIMARY = {
  background:    'linear-gradient(135deg,#4D4E8D,#41427a)',
  border:        'none',
  borderRadius:  8,
  padding:       '9px 18px',
  cursor:        'pointer',
  fontSize:      12,
  fontWeight:    600,
  color:         '#4D4E8D',
  fontFamily:    'inherit',
  letterSpacing: '0.04em',
}

const BTN_SUCCESS = {
  ...BTN_PRIMARY,
  background: 'linear-gradient(135deg,#5E8A72,#3A9A60)',
  color:      '#fff',
}

const BTN_DANGER = {
  background:   'none',
  border:       '1px solid rgba(226,75,74,0.4)',
  borderRadius: 8,
  padding:      '7px 14px',
  cursor:       'pointer',
  fontSize:     11,
  color:        '#E24B4A',
  fontFamily:   'inherit',
}

const BTN_OUTLINE = {
  background:   'none',
  border:       '1px solid #D7DADE',
  borderRadius: 8,
  padding:      '7px 14px',
  cursor:       'pointer',
  fontSize:     11,
  color:        '#5C6065',
  fontFamily:   'inherit',
}

const BTN_PUBLISH_ALL = {
  background:    'linear-gradient(135deg,#4D4E8D,#6E70AE)',
  border:        'none',
  borderRadius:  8,
  padding:       '6px 14px',
  cursor:        'pointer',
  fontSize:      11,
  fontWeight:    700,
  color:         '#151715',
  fontFamily:    'inherit',
  letterSpacing: '0.04em',
  whiteSpace:    'nowrap',
}

// ── LABEL COMPONENT ───────────────────────────────────────────
function Label({ children, required }) {
  return (
    <div style={{ fontSize:11, fontWeight:600, color:'#5C6065', letterSpacing:'0.06em', marginBottom:5 }}>
      {children} {required && <span style={{ color:'#E24B4A' }}>*</span>}
    </div>
  )
}

// ── PUBLISH TOGGLE ────────────────────────────────────────────
function PublishToggle({ value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width:        40,
          height:       22,
          borderRadius: 11,
          background:   value ? '#5E8A72' : '#E2E4E7',
          position:     'relative',
          cursor:       'pointer',
          transition:   'background 0.2s',
          flexShrink:   0,
        }}>
        <div style={{
          width:      18,
          height:     18,
          borderRadius: '50%',
          background: '#fff',
          position:   'absolute',
          top:        2,
          left:       value ? 20 : 2,
          transition: 'left 0.2s',
          boxShadow:  '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{ fontSize:11, color: value ? '#5E8A72' : '#5C6065', fontWeight:600 }}>
        {value ? '✅ Published' : '⏳ Draft'}
      </span>
    </div>
  )
}

// ── 🆕 AUTO-SAVE STATUS BADGE ─────────────────────────────────
// Shows real-time save state to give Nancy visual confidence
function AutoSaveStatus({ status }) {
  if (status === 'idle') return null
  const config = {
    saving:  { text: '💾 Saving...',    color: '#41427a', bg: '#EEF0F8' },
    saved:   { text: '✅ Saved',         color: '#5E8A72', bg: '#EAF2EE' },
    error:   { text: '⚠️ Save failed',   color: '#E24B4A', bg: '#FDF2F2' },
  }[status]
  if (!config) return null
  return (
    <span style={{
      display:      'inline-block',
      fontSize:     10,
      fontWeight:   600,
      color:        config.color,
      background:   config.bg,
      padding:      '3px 10px',
      borderRadius: 12,
      marginLeft:   8,
      transition:   'all 0.2s',
    }}>
      {config.text}
    </span>
  )
}

// ── 🆕 SORTABLE LIST HELPERS ─────────────────────────────────
// Reusable drag-and-drop wrapper for any editor list.
// Renders each item with a drag handle on the left; on drop, calls onReorder
// with the new ordered array of item IDs. Parent decides what to do with it.

function DragHandle({ attributes, listeners, disabled }) {
  return (
    <div
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      title={disabled ? "Drag disabled while saving" : "Drag to reorder"}
      style={{
        cursor: disabled ? 'not-allowed' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        padding: '4px 6px',
        marginRight: 6,
        color: disabled ? '#D8D6CF' : '#A2A6AA',
        fontSize: 18,
        lineHeight: 1,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        opacity: disabled ? 0.4 : 1,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.cursor = 'grabbing' }}
      onMouseUp={(e)   => { if (!disabled) e.currentTarget.style.cursor = 'grab' }}
    >
      ⋮⋮
    </div>
  )
}

function SortableItem({ id, children, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity:  isDragging ? 0.5 : 1,
    zIndex:   isDragging ? 100 : 'auto',
    position: 'relative',
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', alignItems: 'stretch' }}>
        <DragHandle attributes={attributes} listeners={listeners} disabled={disabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

function SortableList({ items, onReorder, disabled, children }) {
  // items = array of objects with .id field, in current order
  // onReorder = async function called with the new ordered array of IDs after drop
  // children = render-prop function (item, index) => JSX for that item (will be wrapped in SortableItem)
  const sensors = useSensors(
    useSensor(PointerSensor,  { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor,    { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function handleDragEnd(event) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex(it => it.id === active.id)
    const newIndex = items.findIndex(it => it.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const newOrder = arrayMove(items, oldIndex, newIndex)
    await onReorder(newOrder)
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(it => it.id)} strategy={verticalListSortingStrategy}>
        {items.map((item, i) => (
          <SortableItem key={item.id} id={item.id} disabled={disabled}>
            {children(item, i)}
          </SortableItem>
        ))}
      </SortableContext>
    </DndContext>
  )
}

// ── QUIZ EDITOR (Module-level) ───────────────────────────────
function QuizEditor({ moduleId, trackId, lessonId }) {
  const isLesson = !!lessonId          // 🆕 per-lesson quiz vs. legacy module quiz
  const scopeId  = lessonId || moduleId // used for unique element IDs
  const [questions, setQuestions] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [adding,    setAdding]    = useState(false)
  const [form,      setForm]      = useState(EMPTY_QUESTION)
  const [saving,    setSaving]    = useState(false)
  const [editId,    setEditId]    = useState(null)
  const [expandedQuestion, setExpandedQuestion] = useState(null)
  // 🆕 Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const hasEditedRef = useRef(false)
  const autoSaveTimerRef = useRef(null)

  useEffect(() => { loadQuestions() }, [moduleId, lessonId])

  // 🆕 AUTO-SAVE EFFECT — 1.5s debounced, only when EDITING existing question
  useEffect(() => {
    if (!hasEditedRef.current) return
    if (!editId) return
    if (!form.question || !form.option_a || !form.option_b) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_quiz_questions')
          .update({ question: form.question, option_a: form.option_a, option_b: form.option_b, option_c: form.option_c, option_d: form.option_d, correct_answer: form.correct_answer })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        loadQuestions()
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[QuizEditor] Auto-save error:', err)
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [form.question, form.option_a, form.option_b, form.option_c, form.option_d, form.correct_answer, editId])

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }

  async function loadQuestions() {
    setLoading(true)
    let query = supabase.from('academy_quiz_questions').select('*')
    if (isLesson) {
      query = query.eq('lesson_id', lessonId)
    } else {
      // Legacy module-level quiz: only questions NOT tied to a specific lesson
      query = query.eq('module_id', moduleId).is('lesson_id', null)
    }
    const { data } = await query.order('sort_order', { ascending: true })
    setQuestions(data || [])
    setLoading(false)
  }

  async function saveQuestion() {
    if (!form.question || !form.option_a || !form.option_b) {
      toast.error('Please fill in the question and at least 2 options!!')
      return
    }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_quiz_questions').update({ ...form }).eq('id', editId)
        toast.success('Question updated!!')
      } else {
        await supabase.from('academy_quiz_questions').insert({
          ...form,
          module_id:  moduleId,
          lesson_id:  lessonId || null,
          track_id:   trackId,
          sort_order: questions.length,
        })
        toast.success('Question added!!')
      }
      setForm(EMPTY_QUESTION)
      setAdding(false)
      setEditId(null)
      hasEditedRef.current = false
      loadQuestions()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteQuestion(id) {
    if (!window.confirm('Delete this question?')) return
    await supabase.from('academy_quiz_questions').delete().eq('id', id)
    toast.success('Question deleted!!')
    loadQuestions()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderQuestions(newOrder) {
    setQuestions(newOrder)
    try {
      const updates = newOrder.map((q, idx) => ({ id: q.id, sort_order: idx }))
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_quiz_questions').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Order saved!!')
    } catch (err) {
      console.error('[QuizEditor] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadQuestions()
    }
  }

  function startEdit(q) {
    setForm({
      question:       q.question,
      option_a:       q.option_a,
      option_b:       q.option_b,
      option_c:       q.option_c || '',
      option_d:       q.option_d || '',
      correct_answer: q.correct_answer,
    })
    setEditId(q.id)
    setAdding(true)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
    setTimeout(() => {
      const formEl = document.getElementById(`quiz-form-${scopeId}`)
      if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  return (
    <div style={{ marginTop:12 }}>
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:8,
        position:'sticky',
        top:0,
        background:'#fff',
        padding:'8px 0',
        zIndex:10,
        borderBottom:'1px solid #EEEFF1',
      }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#151715', letterSpacing:'0.06em' }}>
          📝 {isLesson ? 'QUIZ FOR THIS LESSON' : 'QUIZ QUESTIONS'} ({questions.length})
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_QUESTION); hasEditedRef.current = false; setAutoSaveStatus('idle'); setTimeout(() => {
            const formEl = document.getElementById(`quiz-form-${scopeId}`)
            if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }, 100) }}
            style={{ ...BTN_PRIMARY, padding:'6px 12px', fontSize:11 }}>
            + Add Question
          </button>
        )}
      </div>

      {!loading && (
        <SortableList items={questions} onReorder={reorderQuestions}>
          {(q, i) => {
            const isExpanded = expandedQuestion === q.id
            return (
              <div style={{
                background:   isExpanded ? '#F5F6F7' : '#F7F8F9',
                border:       `1px solid ${isExpanded ? '#4D4E8D' : '#E2E4E7'}`,
                borderRadius: 8,
                padding:      isExpanded ? '12px' : '8px 12px',
                marginBottom: 6,
                transition:   'all 0.15s',
              }}>
                {!isExpanded && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                    <div
                      style={{ flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:8, minWidth:0 }}
                      onClick={() => setExpandedQuestion(q.id)}>
                      <div style={{ width:22, height:22, borderRadius:'50%', background:'#4D4E8D', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, flexShrink:0 }}>
                        {i+1}
                      </div>
                      <div style={{ fontSize:12, color:'#151715', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 }}>
                        {q.question}
                      </div>
                      <span style={{ fontSize:10, color:'#5E8A72', fontWeight:700, flexShrink:0, marginLeft:'auto' }}>
                        ✓ {q.correct_answer?.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                      <button type="button" onClick={() => setExpandedQuestion(q.id)} style={{ ...BTN_OUTLINE, padding:'3px 8px', fontSize:10 }}>▼</button>
                      <button type="button" onClick={() => startEdit(q)} style={{ ...BTN_OUTLINE, padding:'3px 8px', fontSize:10 }}>Edit</button>
                      <button type="button" onClick={() => deleteQuestion(q.id)} style={{ ...BTN_DANGER, padding:'3px 8px', fontSize:10 }}>×</button>
                    </div>
                  </div>
                )}

                {isExpanded && (
                  <div>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8, marginBottom:8 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:8, flex:1 }}>
                        <div style={{ width:22, height:22, borderRadius:'50%', background:'#4D4E8D', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, flexShrink:0, marginTop:2 }}>
                          {i+1}
                        </div>
                        <div style={{ fontSize:13, fontWeight:600, color:'#151715', lineHeight:1.5 }}>{q.question}</div>
                      </div>
                      <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                        <button type="button" onClick={() => setExpandedQuestion(null)} style={{ ...BTN_OUTLINE, padding:'3px 8px', fontSize:10 }}>▲</button>
                        <button type="button" onClick={() => startEdit(q)} style={{ ...BTN_OUTLINE, padding:'4px 10px', fontSize:10 }}>Edit</button>
                        <button type="button" onClick={() => deleteQuestion(q.id)} style={{ ...BTN_DANGER, padding:'4px 10px', fontSize:10 }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ paddingLeft:30, fontSize:12, color:'#3A3D40', lineHeight:1.8 }}>
                      <div style={{ marginBottom:3 }}><strong>A)</strong> {q.option_a}</div>
                      <div style={{ marginBottom:3 }}><strong>B)</strong> {q.option_b}</div>
                      {q.option_c && <div style={{ marginBottom:3 }}><strong>C)</strong> {q.option_c}</div>}
                      {q.option_d && <div style={{ marginBottom:3 }}><strong>D)</strong> {q.option_d}</div>}
                      <div style={{ fontSize:11, color:'#5E8A72', fontWeight:700, marginTop:6 }}>
                        ✓ Correct Answer: {q.correct_answer?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          }}
        </SortableList>
      )}

      {adding && (
        <div id={`quiz-form-${scopeId}`} style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#151715', marginBottom:10 }}>
            {editId ? 'Edit Question' : 'New Question'}
          </div>
          <div style={{ marginBottom:10 }}>
            <Label required>Question</Label>
            <textarea value={form.question} onChange={e => updateForm(f=>({...f,question:e.target.value}))}
              placeholder="e.g. What should you do if a signer's ID is expired?"
              rows={2} style={{ ...TEXTAREA_STYLE, minHeight:60 }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
            {['a','b','c','d'].map(opt => (
              <div key={opt}>
                <Label required={opt === 'a' || opt === 'b'}>Option {opt.toUpperCase()} {(opt==='c'||opt==='d') ? '(optional)' : ''}</Label>
                <input value={form[`option_${opt}`]} onChange={e => updateForm(f=>({...f,[`option_${opt}`]:e.target.value}))}
                  placeholder={`Option ${opt.toUpperCase()}...`} style={INPUT_STYLE} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom:12 }}>
            <Label required>Correct Answer</Label>
            <select value={form.correct_answer} onChange={e => updateForm(f=>({...f,correct_answer:e.target.value}))}
              style={{ ...INPUT_STYLE, width:'auto' }}>
              <option value="a">A — {form.option_a || 'Option A'}</option>
              <option value="b">B — {form.option_b || 'Option B'}</option>
              {form.option_c && <option value="c">C — {form.option_c}</option>}
              {form.option_d && <option value="d">D — {form.option_d}</option>}
            </select>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {editId && <AutoSaveStatus status={autoSaveStatus} />}
            <button type="button" onClick={saveQuestion} disabled={saving} style={BTN_SUCCESS}>
              {saving ? 'Saving...' : editId ? '💾 Update Question' : '💾 Save Question'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setEditId(null); setForm(EMPTY_QUESTION); hasEditedRef.current = false; setAutoSaveStatus('idle'); if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }} style={BTN_OUTLINE}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── 🆕 FINAL EXAM QUESTIONS (per section) ────────────────────
function FinalExamQuestions({ sectionId, trackId, sectionTitle }) {
  const [questions, setQuestions] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [adding,    setAdding]    = useState(false)
  const [form,      setForm]      = useState(EMPTY_QUESTION)
  const [saving,    setSaving]    = useState(false)
  const [editId,    setEditId]    = useState(null)
  const [expandedQuestion, setExpandedQuestion] = useState(null)

  // 🆕 Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const hasEditedRef = useRef(false)
  const autoSaveTimerRef = useRef(null)
  useEffect(() => { loadQuestions() }, [sectionId])
  // 🆕 AUTO-SAVE EFFECT — 1.5s debounced, only when EDITING existing question
  useEffect(() => {
    if (!hasEditedRef.current) return
    if (!editId) return
    if (!form.question || !form.option_a || !form.option_b) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_final_exam_questions')
          .update({ question: form.question, option_a: form.option_a, option_b: form.option_b, option_c: form.option_c, option_d: form.option_d, correct_answer: form.correct_answer })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        loadQuestions()
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[FinalExamQuestions] Auto-save error:', err)
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [form.question, form.option_a, form.option_b, form.option_c, form.option_d, form.correct_answer, editId])

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }


  async function loadQuestions() {
    setLoading(true)
    const { data } = await supabase
      .from('academy_final_exam_questions')
      .select('*')
      .eq('section_id', sectionId)
      .order('sort_order', { ascending: true })
    setQuestions(data || [])
    setLoading(false)
  }

  async function saveQuestion() {
    if (!form.question || !form.option_a || !form.option_b) {
      toast.error('Please fill in the question and at least 2 options!!')
      return
    }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_final_exam_questions').update({ ...form }).eq('id', editId)
        toast.success('Question updated!!')
      } else {
        await supabase.from('academy_final_exam_questions').insert({
          ...form,
          track_id:   trackId,
          section_id: sectionId,
          sort_order: questions.length,
        })
        toast.success('Question added!!')
      }
      setForm(EMPTY_QUESTION)
      setAdding(false)
      setEditId(null)
      loadQuestions()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteQuestion(id) {
    if (!window.confirm('Delete this question?')) return
    await supabase.from('academy_final_exam_questions').delete().eq('id', id)
    toast.success('Question deleted!!')
    loadQuestions()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderQuestions(newOrder) {
    setQuestions(newOrder)
    try {
      const updates = newOrder.map((q, idx) => ({ id: q.id, sort_order: idx }))
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_final_exam_questions').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Order saved!!')
    } catch (err) {
      console.error('[FinalExamQuestions] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadQuestions()
    }
  }

  function startEdit(q) {
    setForm({
      question:       q.question,
      option_a:       q.option_a,
      option_b:       q.option_b,
      option_c:       q.option_c || '',
      option_d:       q.option_d || '',
      correct_answer: q.correct_answer,
    })
    setEditId(q.id)
    setAdding(true)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
    setTimeout(() => {
      const formEl = document.getElementById(`section-form-${sectionId}`)
      if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 100)
  }

  return (
    <div style={{ marginTop:8 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, padding:'6px 10px', background:'rgba(77,78,141,0.08)', borderRadius:6 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#151715', letterSpacing:'0.04em' }}>
          📝 {questions.length} QUESTION{questions.length !== 1 ? 'S' : ''} IN THIS SECTION
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_QUESTION); hasEditedRef.current = false; setAutoSaveStatus('idle'); setTimeout(() => {
            const formEl = document.getElementById(`section-form-${sectionId}`)
            if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }, 100) }}
            style={{ ...BTN_PUBLISH_ALL, padding:'5px 12px', fontSize:10 }}>
            + Add Question
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:11, color:'#A2A6AA', textAlign:'center', padding:10 }}>Loading...</div>}

      {!loading && questions.length === 0 && !adding && (
        <div style={{ textAlign:'center', padding:'20px', color:'#A2A6AA', fontSize:11, fontStyle:'italic' }}>
          No questions in this section yet — click "+ Add Question" above!!
        </div>
      )}

      {!loading && (
        <SortableList items={questions} onReorder={reorderQuestions}>
          {(q, i) => {
            const isExpanded = expandedQuestion === q.id
            return (
              <div style={{
                background:   isExpanded ? '#F5F6F7' : '#F7F8F9',
                border:       `1px solid ${isExpanded ? '#4D4E8D' : '#E2E4E7'}`,
                borderRadius: 6,
                padding:      isExpanded ? '10px' : '6px 10px',
                marginBottom: 4,
                transition:   'all 0.15s',
              }}>
                {!isExpanded && (
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:6 }}>
                    <div
                      style={{ flex:1, cursor:'pointer', display:'flex', alignItems:'center', gap:6, minWidth:0 }}
                      onClick={() => setExpandedQuestion(q.id)}>
                      <div style={{ width:18, height:18, borderRadius:'50%', background:'#4D4E8D', color:'#151715', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, flexShrink:0 }}>
                        {i+1}
                      </div>
                      <div style={{ fontSize:11, color:'#151715', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 }}>
                        {q.question}
                      </div>
                      <span style={{ fontSize:9, color:'#5E8A72', fontWeight:700, flexShrink:0, marginLeft:'auto' }}>
                        ✓ {q.correct_answer?.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:3, flexShrink:0 }}>
                      <button type="button" onClick={() => setExpandedQuestion(q.id)} style={{ ...BTN_OUTLINE, padding:'2px 6px', fontSize:9 }}>▼</button>
                      <button type="button" onClick={() => startEdit(q)} style={{ ...BTN_OUTLINE, padding:'2px 6px', fontSize:9 }}>Edit</button>
                      <button type="button" onClick={() => deleteQuestion(q.id)} style={{ ...BTN_DANGER, padding:'2px 6px', fontSize:9 }}>×</button>
                    </div>
                  </div>
                )}

                {isExpanded && (
                  <div>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:6 }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:6, flex:1 }}>
                        <div style={{ width:18, height:18, borderRadius:'50%', background:'#4D4E8D', color:'#151715', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:700, flexShrink:0, marginTop:2 }}>
                          {i+1}
                        </div>
                        <div style={{ fontSize:12, fontWeight:600, color:'#151715', lineHeight:1.4 }}>{q.question}</div>
                      </div>
                      <div style={{ display:'flex', gap:3, flexShrink:0 }}>
                        <button type="button" onClick={() => setExpandedQuestion(null)} style={{ ...BTN_OUTLINE, padding:'2px 6px', fontSize:9 }}>▲</button>
                        <button type="button" onClick={() => startEdit(q)} style={{ ...BTN_OUTLINE, padding:'3px 8px', fontSize:9 }}>Edit</button>
                        <button type="button" onClick={() => deleteQuestion(q.id)} style={{ ...BTN_DANGER, padding:'3px 8px', fontSize:9 }}>Delete</button>
                      </div>
                    </div>
                    <div style={{ paddingLeft:24, fontSize:11, color:'#3A3D40', lineHeight:1.7 }}>
                      <div style={{ marginBottom:2 }}><strong>A)</strong> {q.option_a}</div>
                      <div style={{ marginBottom:2 }}><strong>B)</strong> {q.option_b}</div>
                      {q.option_c && <div style={{ marginBottom:2 }}><strong>C)</strong> {q.option_c}</div>}
                      {q.option_d && <div style={{ marginBottom:2 }}><strong>D)</strong> {q.option_d}</div>}
                      <div style={{ fontSize:10, color:'#5E8A72', fontWeight:700, marginTop:4 }}>
                        ✓ Correct Answer: {q.correct_answer?.toUpperCase()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          }}
        </SortableList>
      )}

      {adding && (
        <div id={`section-form-${sectionId}`} style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#151715', marginBottom:10 }}>
            {editId ? '🏆 Edit Question' : '🏆 New Question'} — <span style={{ color:'#41427a' }}>{sectionTitle}</span>
          </div>
          <div style={{ marginBottom:10 }}>
            <Label required>Question</Label>
            <textarea value={form.question} onChange={e => updateForm(f=>({...f,question:e.target.value}))}
              placeholder="e.g. The Right of Rescission was established under which federal consumer protection regulation?"
              rows={2} style={{ ...TEXTAREA_STYLE, minHeight:60 }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
            {['a','b','c','d'].map(opt => (
              <div key={opt}>
                <Label required={opt === 'a' || opt === 'b'}>Option {opt.toUpperCase()} {(opt==='c'||opt==='d') ? '(optional)' : ''}</Label>
                <input value={form[`option_${opt}`]} onChange={e => updateForm(f=>({...f,[`option_${opt}`]:e.target.value}))}
                  placeholder={`Option ${opt.toUpperCase()}...`} style={INPUT_STYLE} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom:12 }}>
            <Label required>Correct Answer</Label>
            <select value={form.correct_answer} onChange={e => updateForm(f=>({...f,correct_answer:e.target.value}))}
              style={{ ...INPUT_STYLE, width:'auto' }}>
              <option value="a">A — {form.option_a || 'Option A'}</option>
              <option value="b">B — {form.option_b || 'Option B'}</option>
              {form.option_c && <option value="c">C — {form.option_c}</option>}
              {form.option_d && <option value="d">D — {form.option_d}</option>}
            </select>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {editId && <AutoSaveStatus status={autoSaveStatus} />}
            <button type="button" onClick={saveQuestion} disabled={saving} style={BTN_SUCCESS}>
              {saving ? 'Saving...' : editId ? '💾 Update Question' : '💾 Save Question'}
            </button>
            <button type="button" onClick={() => { setAdding(false); setEditId(null); setForm(EMPTY_QUESTION); hasEditedRef.current = false; setAutoSaveStatus('idle'); if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }} style={BTN_OUTLINE}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── 🆕 FINAL EXAM EDITOR (with SECTIONS!) ────────────────────
function FinalExamEditor({ trackId }) {
  const [sections, setSections] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [adding,   setAdding]   = useState(false)
  const [form,     setForm]     = useState(EMPTY_SECTION)
  const [saving,   setSaving]   = useState(false)
  const [editId,   setEditId]   = useState(null)
  const [expandedSection, setExpandedSection] = useState(null)
  const [questionCounts, setQuestionCounts] = useState({})
  // 🆕 Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const hasEditedRef = useRef(false)
  const autoSaveTimerRef = useRef(null)

  useEffect(() => { loadSections() }, [trackId])

  // 🆕 AUTO-SAVE EFFECT — 1.5s debounced, only when EDITING existing section
  useEffect(() => {
    if (!hasEditedRef.current) return
    if (!editId) return
    if (!form.title) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_final_exam_sections')
          .update({ title: form.title, description: form.description, sort_order: form.sort_order })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        loadSections()
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[FinalExamEditor] Auto-save error:', err)
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [form.title, form.description, form.sort_order, editId])

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }

  async function loadSections() {
    setLoading(true)
    const { data } = await supabase
      .from('academy_final_exam_sections')
      .select('*')
      .eq('track_id', trackId)
      .order('sort_order', { ascending: true })
    setSections(data || [])

    // Load question counts per section
    if (data && data.length > 0) {
      const counts = {}
      for (const sec of data) {
        const { count } = await supabase
          .from('academy_final_exam_questions')
          .select('id', { count: 'exact', head: true })
          .eq('section_id', sec.id)
        counts[sec.id] = count || 0
      }
      setQuestionCounts(counts)
    }
    setLoading(false)
  }

  async function saveSection() {
    if (!form.title) { toast.error('Please enter a section title!!'); return }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_final_exam_sections').update({ ...form }).eq('id', editId)
        toast.success('Section updated!!')
      } else {
        await supabase.from('academy_final_exam_sections').insert({
          ...form,
          track_id:   trackId,
          sort_order: sections.length,
        })
        toast.success('Section added!!')
      }
      setForm(EMPTY_SECTION)
      setAdding(false)
      setEditId(null)
      hasEditedRef.current = false
      loadSections()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteSection(id) {
    if (!window.confirm('Delete this section AND all its questions? This cannot be undone!!')) return
    await supabase.from('academy_final_exam_questions').delete().eq('section_id', id)
    await supabase.from('academy_final_exam_sections').delete().eq('id', id)
    toast.success('Section deleted!!')
    loadSections()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderSections(newOrder) {
    setSections(newOrder)
    try {
      const updates = newOrder.map((s, idx) => ({ id: s.id, sort_order: idx }))
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_final_exam_sections').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Order saved!!')
    } catch (err) {
      console.error('[FinalExamEditor] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadSections()
    }
  }

  function startEdit(sec) {
    setForm({ title: sec.title, description: sec.description || '', sort_order: sec.sort_order })
    setEditId(sec.id)
    setAdding(true)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
  }

  const totalQuestions = Object.values(questionCounts).reduce((a, b) => a + b, 0)

  return (
    <div>
      {/* 🏆 GOLD STICKY HEADER */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:12,
        position:'sticky',
        top:0,
        background:'linear-gradient(135deg,#F5F6F7,#EEF0F8)',
        padding:'10px 14px',
        zIndex:10,
        borderRadius:8,
        border:'1.5px solid #4D4E8D',
        flexWrap:'wrap',
        gap:8,
      }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#151715', letterSpacing:'0.04em' }}>
            🏆 FINAL EXAM SECTIONS ({sections.length}) · {totalQuestions} TOTAL QUESTIONS
          </div>
          <div style={{ fontSize:10, color:'#41427a', marginTop:2 }}>
            80% to pass · Unlimited retakes · Questions scramble within sections 😈
          </div>
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_SECTION); hasEditedRef.current = false; setAutoSaveStatus('idle') }}
            style={BTN_PUBLISH_ALL}>
            + Add Section
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:12, color:'#A2A6AA', textAlign:'center', padding:20 }}>Loading...</div>}

      {!loading && sections.length === 0 && !adding && (
        <div style={{ textAlign:'center', padding:'30px 20px', color:'#A2A6AA', fontSize:12, background:'#F7F8F9', borderRadius:10, border:'1px dashed #E2E4E7' }}>
          🏆 No sections yet!! Click <strong>"+ Add Section"</strong> above to organize your final exam!!
          <div style={{ fontSize:10, marginTop:6, color:'#41427a' }}>
            💡 Example sections: "Core Foundations", "Calendar & Business Days", "Scenarios", "Ethics"
          </div>
        </div>
      )}

      {/* SECTIONS LIST */}
      {!loading && (
        <SortableList items={sections} onReorder={reorderSections}>
          {(sec, i) => (
            <div style={{
              background:   '#fff',
              border:       '1.5px solid #4D4E8D',
              borderRadius: 10,
              marginBottom: 10,
              overflow:     'hidden',
            }}>
              {/* Section header */}
              <div style={{ padding:'12px 14px', background:'linear-gradient(135deg,rgba(77,78,141,0.1),rgba(77,78,141,0.05))', display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#4D4E8D,#6E70AE)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, fontWeight:800, flexShrink:0 }}>
                  {i+1}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:'#151715' }}>
                    {sec.title}
                  </div>
                  {sec.description && <div style={{ fontSize:11, color:'#5C6065', marginTop:2 }}>{sec.description}</div>}
                  <div style={{ fontSize:10, color:'#41427a', marginTop:2, fontWeight:600 }}>
                    📝 {questionCounts[sec.id] || 0} questions
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button type="button"
                    onClick={() => setExpandedSection(expandedSection === sec.id ? null : sec.id)}
                    style={{ ...BTN_OUTLINE, padding:'5px 12px', fontSize:11 }}>
                    {expandedSection === sec.id ? '▲ Close' : '▼ Questions'}
                  </button>
                  <button type="button" onClick={() => startEdit(sec)} style={{ ...BTN_OUTLINE, padding:'5px 10px', fontSize:11 }}>Edit</button>
                  <button type="button" onClick={() => deleteSection(sec.id)} style={{ ...BTN_DANGER, padding:'5px 10px', fontSize:11 }}>Delete</button>
                </div>
              </div>

              {/* Expanded questions */}
              {expandedSection === sec.id && (
                <div style={{ padding:'12px 14px', borderTop:'1px solid #EEEFF1', background:'#F7F8F9' }}>
                  <FinalExamQuestions sectionId={sec.id} trackId={trackId} sectionTitle={sec.title} />
                </div>
              )}
            </div>
          )}
        </SortableList>
      )}

      {/* Add/Edit section form */}
      {adding && (
        <div style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#151715', marginBottom:10 }}>
            {editId ? '✏️ Edit Section' : '🆕 New Section'}
          </div>
          <div style={{ marginBottom:10 }}>
            <Label required>Section Title</Label>
            <input value={form.title} onChange={e => updateForm(f=>({...f,title:e.target.value}))}
              placeholder="e.g. Core Foundations & Rules (Questions 1–15)"
              style={INPUT_STYLE} />
          </div>
          <div style={{ marginBottom:10 }}>
            <Label>Section Description (optional)</Label>
            <textarea value={form.description} onChange={e => updateForm(f=>({...f,description:e.target.value}))}
              placeholder="Optional: Brief description of what this section covers..."
              rows={2} style={{ ...TEXTAREA_STYLE, minHeight:50 }} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center' }}>
            {editId && <AutoSaveStatus status={autoSaveStatus} />}
            <button type="button" onClick={() => { setAdding(false); setEditId(null); setForm(EMPTY_SECTION); hasEditedRef.current = false; setAutoSaveStatus('idle'); if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }} style={BTN_OUTLINE}>
              Cancel
            </button>
            <button type="button" onClick={saveSection} disabled={saving} style={BTN_SUCCESS}>
              {saving ? 'Saving...' : editId ? '💾 Update Section' : '💾 Save Section'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── LESSON EDITOR ─────────────────────────────────────────────
// 🆕 NOW WITH AUTO-SAVE!! Edits are saved automatically 1.5s after you stop typing
// ── 🆕 TRACK RESOURCES EDITOR ────────────────────────────────
// Lets admins attach Resource documents to each Academy track.
// Notaries see these on the track view as "📥 Recommended Resources".
function TrackResourcesEditor({ trackId }) {
  const [attached,         setAttached]         = useState([])    // currently linked docs (with doc info joined)
  const [availableDocs,    setAvailableDocs]    = useState([])    // all published docs not yet attached
  const [loading,          setLoading]          = useState(true)
  const [showPicker,       setShowPicker]       = useState(false)
  const [pickerSearch,     setPickerSearch]     = useState('')
  const [busy,             setBusy]             = useState(false)

  useEffect(() => { loadData() }, [trackId])

  async function loadData() {
    setLoading(true)
    try {
      // Load all published documents
      const { data: allDocs, error: docsErr } = await supabase
        .from('resources_documents')
        .select('*')
        .eq('published', true)
        .order('category', { ascending: true })
        .order('name', { ascending: true })
      if (docsErr) throw docsErr

      // Load currently-attached document IDs for this track
      const { data: links, error: linkErr } = await supabase
        .from('academy_track_resources')
        .select('id, document_id, sort_order')
        .eq('track_id', trackId)
        .order('sort_order', { ascending: true })
      if (linkErr) throw linkErr

      const linkedIds = new Set((links || []).map(l => l.document_id))

      // Build "attached" list (preserve sort_order)
      const docsById = Object.fromEntries((allDocs || []).map(d => [d.id, d]))
      const attachedList = (links || [])
        .map(link => ({ link_id: link.id, sort_order: link.sort_order, ...docsById[link.document_id] }))
        .filter(item => item.id) // filter out any orphaned links (doc was deleted)

      // Build "available" list (all published docs NOT yet attached)
      const availableList = (allDocs || []).filter(d => !linkedIds.has(d.id))

      setAttached(attachedList)
      setAvailableDocs(availableList)
    } catch (err) {
      console.error('[TrackResourcesEditor] Load error:', err)
      toast.error('Could not load track resources: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  async function attachDoc(documentId) {
    setBusy(true)
    try {
      const { error } = await supabase
        .from('academy_track_resources')
        .insert({ track_id: trackId, document_id: documentId, sort_order: attached.length })
      if (error) throw error
      toast.success('Resource attached!!')
      setShowPicker(false)
      setPickerSearch('')
      loadData()
    } catch (err) {
      console.error('[TrackResourcesEditor] Attach error:', err)
      toast.error('Could not attach: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  async function detachDoc(linkId) {
    if (!window.confirm('Remove this resource from this track? (The document itself will NOT be deleted.)')) return
    setBusy(true)
    try {
      const { error } = await supabase
        .from('academy_track_resources')
        .delete()
        .eq('id', linkId)
      if (error) throw error
      toast.success('Resource removed!!')
      loadData()
    } catch (err) {
      console.error('[TrackResourcesEditor] Detach error:', err)
      toast.error('Could not remove: ' + err.message)
    } finally {
      setBusy(false)
    }
  }

  // File-icon for a given type
  function fileIcon(fileType) {
    if (!fileType) return '📄'
    const t = fileType.toLowerCase()
    if (t.includes('pdf')) return '📕'
    if (t.includes('word') || t.includes('doc')) return '📘'
    if (t.includes('excel') || t.includes('sheet') || t.includes('xls')) return '📗'
    if (t.includes('image') || t.includes('png') || t.includes('jpg')) return '🖼️'
    return '📄'
  }

  // Filter for the picker
  const filteredAvailable = pickerSearch
    ? availableDocs.filter(d =>
        d.name?.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        d.category?.toLowerCase().includes(pickerSearch.toLowerCase()) ||
        d.description?.toLowerCase().includes(pickerSearch.toLowerCase())
      )
    : availableDocs

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#151715', letterSpacing:'0.04em' }}>
            📥 RECOMMENDED RESOURCES ({attached.length})
          </div>
          <div style={{ fontSize:11, color:'#5C6065', marginTop:2 }}>
            Documents notaries will see on this track's page
          </div>
        </div>
        {!showPicker && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
            <button type="button"
              onClick={() => { setShowPicker(true); setPickerSearch('') }}
              disabled={busy || loading || availableDocs.length === 0}
              style={{ ...BTN_PRIMARY, padding:'6px 14px', fontSize:11, opacity: (busy || loading || availableDocs.length === 0) ? 0.5 : 1 }}>
              + Add Resource
            </button>
            {!loading && availableDocs.length === 0 && (
              <div style={{ fontSize:10, color:'#41427a', fontStyle:'italic', textAlign:'right', maxWidth:200 }}>
                All published documents already attached. Add more in the Resources page!
              </div>
            )}
          </div>
        )}
      </div>

      {loading && <div style={{ fontSize:12, color:'#A2A6AA' }}>Loading resources...</div>}

      {/* Attached docs list */}
      {!loading && attached.length === 0 && !showPicker && (
        <div style={{ textAlign:'center', padding:'24px 16px', color:'#A2A6AA', fontSize:12, background:'#F7F8F9', border:'1px dashed #E2E4E7', borderRadius:8 }}>
          No resources attached yet. Click "+ Add Resource" to recommend documents for this track.
        </div>
      )}

      {!loading && attached.map((doc) => (
        <div key={doc.link_id} style={{
          display:'flex',
          alignItems:'center',
          gap:12,
          padding:'10px 14px',
          background:'#F7F8F9',
          border:'1px solid #E2E4E7',
          borderRadius:8,
          marginBottom:6,
        }}>
          <div style={{ fontSize:22, flexShrink:0 }}>{fileIcon(doc.file_type)}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#151715', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {doc.name}
            </div>
            {doc.category && (
              <div style={{ fontSize:10, color:'#41427a', fontWeight:600, marginTop:2 }}>
                {doc.category}
              </div>
            )}
            {doc.description && (
              <div style={{ fontSize:11, color:'#5C6065', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {doc.description}
              </div>
            )}
          </div>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            {doc.file_url && (
              <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                style={{ ...BTN_OUTLINE, padding:'5px 10px', fontSize:11, textDecoration:'none', display:'inline-block' }}>
                Preview
              </a>
            )}
            <button type="button" onClick={() => detachDoc(doc.link_id)} disabled={busy}
              style={{ ...BTN_DANGER, padding:'5px 10px', fontSize:11 }}>
              Remove
            </button>
          </div>
        </div>
      ))}

      {/* Picker (modal-ish inline panel) */}
      {showPicker && (
        <div style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#151715' }}>
              Pick a document to attach
            </div>
            <button type="button" onClick={() => { setShowPicker(false); setPickerSearch('') }}
              style={{ ...BTN_OUTLINE, padding:'4px 10px', fontSize:11 }}>
              ✕ Close
            </button>
          </div>
          <input
            value={pickerSearch}
            onChange={e => setPickerSearch(e.target.value)}
            placeholder="🔍 Search by name, category, or description..."
            style={{ ...INPUT_STYLE, marginBottom:10 }}
          />
          {availableDocs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px', color:'#5C6065', fontSize:12 }}>
              No more documents available to attach. (All published documents are already linked.)
            </div>
          ) : filteredAvailable.length === 0 ? (
            <div style={{ textAlign:'center', padding:'20px', color:'#5C6065', fontSize:12 }}>
              No documents match your search.
            </div>
          ) : (
            <div style={{ maxHeight:300, overflowY:'auto' }}>
              {filteredAvailable.map(doc => (
                <div key={doc.id} style={{
                  display:'flex',
                  alignItems:'center',
                  gap:10,
                  padding:'10px 12px',
                  background:'#fff',
                  border:'1px solid #E2E4E7',
                  borderRadius:8,
                  marginBottom:6,
                  cursor: busy ? 'wait' : 'pointer',
                }}
                onClick={() => !busy && attachDoc(doc.id)}>
                  <div style={{ fontSize:20, flexShrink:0 }}>{fileIcon(doc.file_type)}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'#151715', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {doc.name}
                    </div>
                    {doc.category && (
                      <div style={{ fontSize:10, color:'#41427a', fontWeight:600, marginTop:1 }}>
                        {doc.category}
                      </div>
                    )}
                    {doc.description && (
                      <div style={{ fontSize:11, color:'#5C6065', marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {doc.description}
                      </div>
                    )}
                  </div>
                  <div style={{ flexShrink:0 }}>
                    <span style={{ fontSize:11, color:'#4D4E8D', fontWeight:600 }}>+ Attach</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LessonEditor({ moduleId, trackId }) {
  const [lessons,  setLessons]  = useState([])
  const [loading,  setLoading]  = useState(true)
  const [editId,   setEditId]   = useState(null)
  const [form,     setForm]     = useState(EMPTY_LESSON)
  const [adding,   setAdding]   = useState(false)
  const [saving,   setSaving]   = useState(false)
  const [expanded, setExpanded] = useState(null)
  // 🆕 Auto-save status: 'idle' | 'saving' | 'saved' | 'error'
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  // 🆕 Track if we've made any edits yet (so we don't save on initial load)
  const hasEditedRef = useRef(false)
  // 🆕 Debounce timer ref
  const autoSaveTimerRef = useRef(null)

  useEffect(() => { loadLessons() }, [moduleId])

  // 🆕 AUTO-SAVE EFFECT — Fires 1.5 seconds after the form changes (debounced)
  // Only auto-saves when EDITING an existing lesson (not when creating new)
  useEffect(() => {
    // Don't auto-save until user has made at least one edit
    if (!hasEditedRef.current) return
    // Only auto-save existing lessons (not new ones being created)
    if (!editId) return
    // Don't auto-save if title or content is empty
    if (!form.title || !form.content) return

    // Clear any pending save
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)

    // Schedule new save 1.5 seconds from now
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_lessons')
          .update({
            title:      form.title,
            content:    form.content,
            published:  form.published,
            sort_order: form.sort_order,
          })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        // Refresh the lessons list so the inline display stays in sync
        loadLessons()
        // Hide "Saved" badge after 2 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[LessonEditor] Auto-save error:', err)
        setAutoSaveStatus('error')
        // Hide error badge after 4 seconds (longer so it's noticed)
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)

    // Cleanup: cancel pending save if component unmounts or deps change
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    }
  }, [form.title, form.content, form.published, form.sort_order, editId])

  async function loadLessons() {
    setLoading(true)
    const { data } = await supabase
      .from('academy_lessons')
      .select('*')
      .eq('module_id', moduleId)
      .order('sort_order', { ascending: true })
    setLessons(data || [])
    setLoading(false)
  }

  async function saveLesson() {
    if (!form.title || !form.content) { toast.error('Please fill in title and content!!'); return }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_lessons').update({ ...form }).eq('id', editId)
        toast.success('Lesson updated!!')
      } else {
        await supabase.from('academy_lessons').insert({
          ...form,
          module_id:  moduleId,
          track_id:   trackId,
          sort_order: lessons.length,
        })
        toast.success('Lesson added!!')
      }
      setForm(EMPTY_LESSON)
      setAdding(false)
      setEditId(null)
      hasEditedRef.current = false // Reset edit tracking
      setAutoSaveStatus('idle')
      loadLessons()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteLesson(id) {
    if (!window.confirm('Delete this lesson? This cannot be undone!!')) return
    await supabase.from('academy_lessons').delete().eq('id', id)
    toast.success('Lesson deleted!!')
    loadLessons()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderLessons(newOrder) {
    setLessons(newOrder)
    try {
      const updates = newOrder.map((l, idx) => ({ id: l.id, sort_order: idx }))
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_lessons').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Order saved!!')
    } catch (err) {
      console.error('[LessonEditor] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadLessons()
    }
  }

  function startEdit(lesson) {
    setForm({
      title:      lesson.title,
      content:    lesson.content,
      sort_order: lesson.sort_order,
      published:  lesson.published,
    })
    setEditId(lesson.id)
    setAdding(true)
    hasEditedRef.current = false // Reset — won't auto-save until user types
    setAutoSaveStatus('idle')
  }

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }

  function cancelEdit() {
    // Clear any pending auto-save
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    setAdding(false)
    setEditId(null)
    setForm(EMPTY_LESSON)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#151715', letterSpacing:'0.06em' }}>
          📖 LESSONS ({lessons.length})
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_LESSON); hasEditedRef.current = false; setAutoSaveStatus('idle') }}
            style={{ ...BTN_PRIMARY, padding:'6px 12px', fontSize:11 }}>
            + Add Lesson
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:12, color:'#A2A6AA' }}>Loading...</div>}
      {!loading && (
        <SortableList items={lessons} onReorder={reorderLessons}>
          {(lesson, i) => (
            <div style={{
              background:   '#fff',
              border:       `1px solid ${lesson.published ? 'rgba(45,122,79,0.25)' : '#E2E4E7'}`,
              borderRadius: 8,
              marginBottom: 6,
              overflow:     'hidden',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', cursor:'pointer' }}
                onClick={() => setExpanded(expanded === lesson.id ? null : lesson.id)}>
                <div style={{ width:22, height:22, borderRadius:'50%', background:'#EEEFF1', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#5C6065', flexShrink:0 }}>{i+1}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:500, color:'#151715' }}>{lesson.title}</div>
                  <div style={{ fontSize:10, color: lesson.published ? '#5E8A72' : '#A2A6AA', fontWeight:600 }}>
                    {lesson.published ? '✅ Published' : '⏳ Draft'}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button type="button" onClick={e => { e.stopPropagation(); startEdit(lesson) }} style={{ ...BTN_OUTLINE, padding:'4px 10px', fontSize:10 }}>Edit</button>
                  <button type="button" onClick={e => { e.stopPropagation(); deleteLesson(lesson.id) }} style={{ ...BTN_DANGER, padding:'4px 10px', fontSize:10 }}>Delete</button>
                </div>
              </div>
              {expanded === lesson.id && (
                <div style={{ borderTop:'1px solid #EEEFF1' }}>
                  <div style={{ padding:'12px 12px', fontSize:12, color:'#5C6065', lineHeight:1.7, whiteSpace:'pre-line' }}>
                    {lesson.content}
                  </div>
                  {/* 🆕 PER-LESSON QUIZ — questions tied to THIS lesson. Travels with the lesson when dragged. */}
                  <div style={{ background:'#F5F6F7', borderTop:'2px solid #4D4E8D', padding:'12px 12px 14px' }}>
                    <QuizEditor moduleId={moduleId} trackId={trackId} lessonId={lesson.id} />
                  </div>
                </div>
              )}
            </div>
          )}
        </SortableList>
      )}

      {adding && (
        <div style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#151715', marginBottom:10, display:'flex', alignItems:'center' }}>
            {editId ? '✏️ Edit Lesson' : '✏️ New Lesson'}
            {/* 🆕 Auto-save indicator — only shows when editing existing lesson */}
            {editId && <AutoSaveStatus status={autoSaveStatus} />}
          </div>
          {editId && (
            <div style={{ fontSize:10, color:'#41427a', marginBottom:10, fontStyle:'italic' }}>
              💾 Auto-save is ON. Changes save automatically 1.5 seconds after you stop typing.
            </div>
          )}
          <div style={{ marginBottom:10 }}>
            <Label required>Lesson Title</Label>
            <input value={form.title} onChange={e => updateForm(f=>({...f,title:e.target.value}))}
              placeholder="e.g. Professionalism at the Signing Table"
              style={INPUT_STYLE} />
          </div>
          <div style={{ marginBottom:10 }}>
            <Label required>Lesson Content</Label>
            <div style={{ fontSize:10, color:'#A2A6AA', marginBottom:4 }}>
              Write your lesson content below. Use blank lines to separate paragraphs. Use • for bullet points.
            </div>
            <textarea value={form.content} onChange={e => updateForm(f=>({...f,content:e.target.value}))}
              placeholder="Write your full lesson content here...&#10;&#10;Use blank lines between sections.&#10;• Use bullet points like this&#10;• For lists of items"
              rows={12} style={TEXTAREA_STYLE} />
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
            <PublishToggle value={form.published} onChange={v => updateForm(f=>({...f,published:v}))} />
            <div style={{ display:'flex', gap:8 }}>
              <button type="button" onClick={saveLesson} disabled={saving} style={BTN_SUCCESS}>
                {saving ? 'Saving...' : editId ? '💾 Save & Close' : '💾 Save Lesson'}
              </button>
              <button type="button" onClick={cancelEdit} style={BTN_OUTLINE}>
                {editId ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── MODULE EDITOR ─────────────────────────────────────────────
function ModuleEditor({ trackId }) {
  const [modules,       setModules]       = useState([])
  const [loading,       setLoading]       = useState(true)
  const [adding,        setAdding]        = useState(false)
  const [editId,        setEditId]        = useState(null)
  const [form,          setForm]          = useState(EMPTY_MODULE)
  const [saving,        setSaving]        = useState(false)
  const [expandedMod,   setExpandedMod]   = useState(null)
  // 🆕 Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const hasEditedRef = useRef(false)
  const autoSaveTimerRef = useRef(null)

  useEffect(() => { loadModules() }, [trackId])

  // 🆕 AUTO-SAVE EFFECT — 1.5s debounced, only when EDITING existing module
  useEffect(() => {
    if (!hasEditedRef.current) return
    if (!editId) return
    if (!form.title) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_modules')
          .update({ title: form.title, icon: form.icon, sort_order: form.sort_order, published: form.published })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        loadModules()
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[ModuleEditor] Auto-save error:', err)
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [form.title, form.icon, form.sort_order, form.published, editId])

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }

  async function loadModules() {
    setLoading(true)
    const { data } = await supabase
      .from('academy_modules')
      .select('*')
      .eq('track_id', trackId)
      .order('sort_order', { ascending: true })
    setModules(data || [])
    setLoading(false)
  }

  async function saveModule() {
    if (!form.title) { toast.error('Please enter a module title!!'); return }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_modules').update({ ...form }).eq('id', editId)
        toast.success('Module updated!!')
      } else {
        await supabase.from('academy_modules').insert({
          ...form,
          track_id:   trackId,
          sort_order: modules.length,
        })
        toast.success('Module added!!')
      }
      setForm(EMPTY_MODULE)
      setAdding(false)
      setEditId(null)
      hasEditedRef.current = false
      loadModules()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteModule(id) {
    if (!window.confirm('Delete this module and ALL its lessons and quiz questions? This cannot be undone!!')) return
    await supabase.from('academy_lessons').delete().eq('module_id', id)
    await supabase.from('academy_quiz_questions').delete().eq('module_id', id)
    await supabase.from('academy_modules').delete().eq('id', id)
    toast.success('Module deleted!!')
    loadModules()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderModules(newOrder) {
    // Optimistic UI update
    setModules(newOrder)
    try {
      // Update sort_order for each row that moved
      const updates = newOrder.map((m, idx) => ({ id: m.id, sort_order: idx }))
      // Run updates in parallel for speed
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_modules').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Order saved!!')
    } catch (err) {
      console.error('[ModuleEditor] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadModules() // revert by re-fetching
    }
  }

  function startEdit(mod) {
    setForm({ title: mod.title, icon: mod.icon, sort_order: mod.sort_order, published: mod.published })
    setEditId(mod.id)
    setAdding(true)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
  }

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#151715', letterSpacing:'0.06em' }}>
          📦 MODULES ({modules.length})
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_MODULE); hasEditedRef.current = false; setAutoSaveStatus('idle') }}
            style={{ ...BTN_PRIMARY, padding:'6px 12px', fontSize:11 }}>
            + Add Module
          </button>
        )}
      </div>

      {loading && <div style={{ fontSize:12, color:'#A2A6AA' }}>Loading...</div>}

      {!loading && (
        <SortableList items={modules} onReorder={reorderModules}>
          {(mod, i) => (
            <div style={{
              background:   '#fff',
              border:       `1.5px solid ${mod.published ? 'rgba(45,122,79,0.3)' : '#E2E4E7'}`,
              borderRadius: 10,
              marginBottom: 8,
              overflow:     'hidden',
            }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 14px', background:'#F7F8F9' }}>
                <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#4D4E8D,#41427a)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                  {mod.icon}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#151715' }}>
                    Module {i+1}: {mod.title}
                  </div>
                  <div style={{ fontSize:10, color: mod.published ? '#5E8A72' : '#A2A6AA', fontWeight:600, marginTop:1 }}>
                    {mod.published ? '✅ Published' : '⏳ Draft'}
                  </div>
                </div>
                <div style={{ display:'flex', gap:6, flexShrink:0 }}>
                  <button type="button"
                    onClick={() => setExpandedMod(expandedMod === mod.id ? null : mod.id)}
                    style={{ ...BTN_OUTLINE, padding:'5px 12px', fontSize:11 }}>
                    {expandedMod === mod.id ? '▲ Close' : '▼ Lessons & Quiz'}
                  </button>
                  <button type="button" onClick={() => startEdit(mod)} style={{ ...BTN_OUTLINE, padding:'5px 10px', fontSize:11 }}>Edit</button>
                  <button type="button" onClick={() => deleteModule(mod.id)} style={{ ...BTN_DANGER, padding:'5px 10px', fontSize:11 }}>Delete</button>
                </div>
              </div>

              {expandedMod === mod.id && (
                <div style={{ padding:'14px', borderTop:'1px solid #EEEFF1' }}>
                  <LessonEditor moduleId={mod.id} trackId={trackId} />
                  <div style={{ height:1, background:'#E2E4E7', margin:'16px 0' }} />
                  <div style={{ fontSize:10, color:'#41427a', background:'#EEF0F8', border:'1px solid #E8D9A8', borderRadius:6, padding:'6px 10px', marginBottom:8 }}>
                    💡 Optional: a module-wide quiz. Most courses now use the <strong>Quiz for this lesson</strong> under each lesson above — leave this empty if you do.
                  </div>
                  <QuizEditor moduleId={mod.id} trackId={trackId} />
                </div>
              )}
            </div>
          )}
        </SortableList>
      )}

      {adding && (
        <div style={{ background:'#F5F6F7', border:'1.5px solid #4D4E8D', borderRadius:10, padding:'14px', marginTop:8 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#151715', marginBottom:10 }}>
            {editId ? '✏️ Edit Module' : '✏️ New Module'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:10, marginBottom:10, alignItems:'end' }}>
            <div>
              <Label required>Module Title</Label>
              <input value={form.title} onChange={e => updateForm(f=>({...f,title:e.target.value}))}
                placeholder="e.g. Introduction to Notarial Work"
                style={INPUT_STYLE} />
            </div>
            <div>
              <Label>Icon</Label>
              <select value={form.icon} onChange={e => updateForm(f=>({...f,icon:e.target.value}))}
                style={{ ...INPUT_STYLE, width:'auto', fontSize:18 }}>
                {ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
            <PublishToggle value={form.published} onChange={v => updateForm(f=>({...f,published:v}))} />
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {editId && <AutoSaveStatus status={autoSaveStatus} />}
              <button type="button" onClick={saveModule} disabled={saving} style={BTN_SUCCESS}>
                {saving ? 'Saving...' : editId ? '💾 Update' : '💾 Save Module'}
              </button>
              <button type="button" onClick={() => { setAdding(false); setEditId(null); setForm(EMPTY_MODULE); hasEditedRef.current = false; setAutoSaveStatus('idle'); if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }} style={BTN_OUTLINE}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── MAIN ACADEMY CMS ──────────────────────────────────────────
export default function AcademyCMS() {
  const [tracks,      setTracks]      = useState([])
  const [loading,     setLoading]     = useState(true)
  const [adding,      setAdding]      = useState(false)
  const [editId,      setEditId]      = useState(null)
  const [form,        setForm]        = useState(EMPTY_TRACK)
  const [saving,      setSaving]      = useState(false)
  const [expandedTrack, setExpandedTrack] = useState(null)
  const [expandedFinalExam, setExpandedFinalExam] = useState(null)
  const [expandedResources, setExpandedResources] = useState(null)
  const [cascadingTrackId, setCascadingTrackId] = useState(null)
  // 🆕 Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle')
  const hasEditedRef = useRef(false)
  const autoSaveTimerRef = useRef(null)

  useEffect(() => { loadTracks() }, [])

  // 🆕 AUTO-SAVE EFFECT — 1.5s debounced, only when EDITING existing track
  useEffect(() => {
    if (!hasEditedRef.current) return
    if (!editId) return
    if (!form.title || !form.subtitle) return
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current)
    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        const { error } = await supabase
          .from('academy_tracks')
          .update({
            title:             form.title,
            subtitle:          form.subtitle,
            description:       form.description,
            icon:              form.icon,
            color:             form.color,
            published:         form.published,
            sort_order:        form.sort_order,
            final_exam_title:  form.final_exam_title,
            final_exam_intro:  form.final_exam_intro,
          })
          .eq('id', editId)
        if (error) throw error
        setAutoSaveStatus('saved')
        loadTracks()
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (err) {
        console.error('[TrackEditor] Auto-save error:', err)
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 4000)
      }
    }, 1500)
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }
  }, [form.title, form.subtitle, form.description, form.icon, form.color, form.published, form.sort_order, form.final_exam_title, form.final_exam_intro, editId])

  // 🆕 Helper that wraps form updates and marks "user has edited"
  function updateForm(updater) {
    hasEditedRef.current = true
    setForm(updater)
  }

  async function loadTracks() {
    setLoading(true)
    const { data } = await supabase
      .from('academy_tracks')
      .select('*')
      .order('sort_order', { ascending: true })
    setTracks(data || [])
    setLoading(false)
  }

  async function publishAllCascade(track) {
    setCascadingTrackId(track.id)
    try {
      await supabase.from('academy_tracks').update({ published: true }).eq('id', track.id)
      const { data: mods } = await supabase
        .from('academy_modules')
        .select('id')
        .eq('track_id', track.id)
      const modCount = mods?.length || 0
      if (modCount > 0) {
        await supabase
          .from('academy_modules')
          .update({ published: true })
          .eq('track_id', track.id)
      }
      let lessonCount = 0
      if (modCount > 0) {
        const modIds = mods.map(m => m.id)
        const { data: lessons } = await supabase
          .from('academy_lessons')
          .select('id')
          .in('module_id', modIds)
        lessonCount = lessons?.length || 0
        if (lessonCount > 0) {
          await supabase
            .from('academy_lessons')
            .update({ published: true })
            .in('module_id', modIds)
        }
      }
      toast.success(
        `🚀 Published 1 track + ${modCount} module${modCount !== 1 ? 's' : ''} + ${lessonCount} lesson${lessonCount !== 1 ? 's' : ''}!!`,
        { duration: 5000 }
      )
      loadTracks()
    } catch (err) {
      toast.error('Error publishing all: ' + err.message)
    } finally {
      setCascadingTrackId(null)
    }
  }

  async function unpublishAllCascade(track) {
    if (!window.confirm(`Unpublish entire track "${track.title}" and ALL its modules + lessons?`)) return
    setCascadingTrackId(track.id)
    try {
      await supabase.from('academy_tracks').update({ published: false }).eq('id', track.id)
      const { data: mods } = await supabase.from('academy_modules').select('id').eq('track_id', track.id)
      const modCount = mods?.length || 0
      if (modCount > 0) {
        await supabase.from('academy_modules').update({ published: false }).eq('track_id', track.id)
      }
      let lessonCount = 0
      if (modCount > 0) {
        const modIds = mods.map(m => m.id)
        const { data: lessons } = await supabase.from('academy_lessons').select('id').in('module_id', modIds)
        lessonCount = lessons?.length || 0
        if (lessonCount > 0) {
          await supabase.from('academy_lessons').update({ published: false }).in('module_id', modIds)
        }
      }
      toast.success(`📴 Unpublished 1 track + ${modCount} modules + ${lessonCount} lessons`, { duration: 4000 })
      loadTracks()
    } catch (err) {
      toast.error('Error unpublishing all: ' + err.message)
    } finally {
      setCascadingTrackId(null)
    }
  }

  async function saveTrack() {
    if (!form.title || !form.subtitle) { toast.error('Please fill in track title and subtitle!!'); return }
    setSaving(true)
    try {
      if (editId) {
        await supabase.from('academy_tracks').update({ ...form }).eq('id', editId)
        toast.success('Track updated!!')
      } else {
        await supabase.from('academy_tracks').insert({ ...form, sort_order: tracks.length })
        toast.success('Track created!!')
      }
      setForm(EMPTY_TRACK)
      setAdding(false)
      setEditId(null)
      hasEditedRef.current = false
      loadTracks()
    } catch (err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function deleteTrack(id) {
    if (!window.confirm('Delete this track and ALL its modules, lessons, quiz questions, and final exam? This cannot be undone!!')) return
    const { data: mods } = await supabase.from('academy_modules').select('id').eq('track_id', id)
    if (mods?.length) {
      const modIds = mods.map(m => m.id)
      await supabase.from('academy_lessons').delete().in('module_id', modIds)
      await supabase.from('academy_quiz_questions').delete().in('module_id', modIds)
    }
    // Delete final exam sections AND questions
    const { data: secs } = await supabase.from('academy_final_exam_sections').select('id').eq('track_id', id)
    if (secs?.length) {
      const secIds = secs.map(s => s.id)
      await supabase.from('academy_final_exam_questions').delete().in('section_id', secIds)
    }
    await supabase.from('academy_final_exam_sections').delete().eq('track_id', id)
    await supabase.from('academy_final_exam_questions').delete().eq('track_id', id)
    await supabase.from('academy_modules').delete().eq('track_id', id)
    await supabase.from('academy_tracks').delete().eq('id', id)
    toast.success('Track deleted!!')
    loadTracks()
  }

  async function togglePublish(track) {
    await supabase.from('academy_tracks').update({ published: !track.published }).eq('id', track.id)
    toast.success(`Track ${!track.published ? 'published' : 'unpublished'}!!`)
    loadTracks()
  }

  // 🆕 Drag-and-drop reorder
  async function reorderTracks(newOrder) {
    setTracks(newOrder)
    try {
      const updates = newOrder.map((t, idx) => ({ id: t.id, sort_order: idx }))
      const results = await Promise.all(
        updates.map(u =>
          supabase.from('academy_tracks').update({ sort_order: u.sort_order }).eq('id', u.id)
        )
      )
      const failed = results.filter(r => r.error)
      if (failed.length > 0) throw new Error(failed[0].error.message)
      toast.success('Track order saved!!')
    } catch (err) {
      console.error('[TrackEditor] Reorder error:', err)
      toast.error('Could not save order: ' + err.message)
      loadTracks()
    }
  }

  function startEdit(track) {
    setForm({
      title:             track.title,
      subtitle:          track.subtitle,
      description:       track.description,
      icon:              track.icon,
      color:             track.color,
      published:         track.published,
      sort_order:        track.sort_order,
      final_exam_title:  track.final_exam_title || '',
      final_exam_intro:  track.final_exam_intro || '',
    })
    setEditId(track.id)
    setAdding(true)
    hasEditedRef.current = false
    setAutoSaveStatus('idle')
  }

  if (loading) return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:200 }}><div className="spinner" /></div>

  return (
    <main tabIndex={-1} style={{ maxWidth:820, margin:'0 auto' }}>

      {/* ── HERO ── */}
      <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:14, padding:'20px 24px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#fff', fontWeight:600, marginBottom:4 }}>
              🎓 Education CMS
            </div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)' }}>
              Build and manage your notary school curriculum. No deployment needed — publish instantly!!
            </div>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:9, color:'rgba(255,255,255,0.4)', letterSpacing:'0.1em' }}>TRACKS</div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#fff', fontWeight:700 }}>{tracks.length}</div>
            </div>
            <div style={{ textAlign:'right' }}>
              <div style={{ fontSize:9, color:'rgba(255,255,255,0.4)', letterSpacing:'0.1em' }}>PUBLISHED</div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:24, color:'#5BA882', fontWeight:700 }}>
                {tracks.filter(t => t.published).length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ADD TRACK BUTTON ── */}
      {!adding && (
        <button type="button"
          onClick={() => { setAdding(true); setEditId(null); setForm(EMPTY_TRACK); hasEditedRef.current = false; setAutoSaveStatus('idle') }}
          style={{ ...BTN_PRIMARY, width:'100%', padding:'14px', fontSize:14, marginBottom:16, borderRadius:12, textAlign:'center' }}>
          ＋ Add New Track
        </button>
      )}

      {/* ── ADD/EDIT TRACK FORM ── */}
      {adding && (
        <div style={{ background:'#fff', border:'1.5px solid #4D4E8D', borderRadius:14, padding:'20px', marginBottom:16 }}>
          <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, color:'#151715', fontWeight:600, marginBottom:14 }}>
            {editId ? '✏️ Edit Track' : '✏️ New Track'}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <Label required>Track Title</Label>
              <input value={form.title} onChange={e => updateForm(f=>({...f,title:e.target.value}))}
                placeholder="e.g. Notary Foundations"
                style={INPUT_STYLE} />
            </div>
            <div>
              <Label required>Subtitle</Label>
              <input value={form.subtitle} onChange={e => updateForm(f=>({...f,subtitle:e.target.value}))}
                placeholder="e.g. Beginner Track"
                style={INPUT_STYLE} />
            </div>
            <div style={{ gridColumn:'1/-1' }}>
              <Label>Description</Label>
              <textarea value={form.description} onChange={e => updateForm(f=>({...f,description:e.target.value}))}
                placeholder="Brief description of what notaries will learn in this track..."
                rows={3} style={TEXTAREA_STYLE} />
            </div>
            <div>
              <Label>Icon</Label>
              <select value={form.icon} onChange={e => updateForm(f=>({...f,icon:e.target.value}))}
                style={{ ...INPUT_STYLE, fontSize:18 }}>
                {ICONS.map(ic => <option key={ic} value={ic}>{ic} {ic}</option>)}
              </select>
            </div>
            <div>
              <Label>Color</Label>
              <select value={form.color} onChange={e => updateForm(f=>({...f,color:e.target.value}))}
                style={INPUT_STYLE}>
                {COLORS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* 🆕 FINAL EXAM CUSTOM TITLE + INTRO */}
          <div style={{ background:'linear-gradient(135deg,#F5F6F7,#EEF0F8)', border:'1px solid #4D4E8D', borderRadius:10, padding:'14px', marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#41427a', letterSpacing:'0.06em', marginBottom:10 }}>
              🏆 FINAL EXAM CUSTOM HEADING (OPTIONAL)
            </div>
            <div style={{ marginBottom:10 }}>
              <Label>Custom Test Title</Label>
              <input value={form.final_exam_title} onChange={e => updateForm(f=>({...f,final_exam_title:e.target.value}))}
                placeholder="e.g. NSV Certificate of Mastery: Rescission Calendar Proficiency"
                style={INPUT_STYLE} />
              <div style={{ fontSize:10, color:'#41427a', marginTop:4, fontStyle:'italic' }}>
                💡 This appears as the main heading on the notary's final exam page
              </div>
            </div>
            <div>
              <Label>Custom Test Intro / Subtitle</Label>
              <input value={form.final_exam_intro} onChange={e => updateForm(f=>({...f,final_exam_intro:e.target.value}))}
                placeholder="e.g. Final Examination (50 Questions)"
                style={INPUT_STYLE} />
              <div style={{ fontSize:10, color:'#41427a', marginTop:4, fontStyle:'italic' }}>
                💡 This appears as a subtitle under the test title
              </div>
            </div>
          </div>

          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
            <PublishToggle value={form.published} onChange={v => updateForm(f=>({...f,published:v}))} />
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {editId && <AutoSaveStatus status={autoSaveStatus} />}
              <button type="button" onClick={saveTrack} disabled={saving} style={{ ...BTN_SUCCESS, padding:'10px 20px', fontSize:13 }}>
                {saving ? 'Saving...' : editId ? '💾 Update Track' : '💾 Create Track'}
              </button>
              <button type="button" onClick={() => { setAdding(false); setEditId(null); setForm(EMPTY_TRACK); hasEditedRef.current = false; setAutoSaveStatus('idle'); if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current) }} style={{ ...BTN_OUTLINE, padding:'10px 16px', fontSize:13 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TRACKS LIST ── */}
      {tracks.length === 0 && !adding && (
        <div style={{ textAlign:'center', padding:'40px 20px', color:'#A2A6AA', fontSize:13 }}>
          No tracks yet!! Click "Add New Track" to build your first curriculum track!! 🎓
        </div>
      )}

      {!loading && (
        <SortableList items={tracks} onReorder={reorderTracks}>
          {(track, i) => (
        <div style={{
          background:   '#fff',
          border:       `1.5px solid ${track.published ? track.color + '50' : '#E2E4E7'}`,
          borderRadius: 14,
          overflow:     'hidden',
          marginBottom: 12,
        }}>
          {/* Track header */}
          <div style={{
            background:  `linear-gradient(135deg,${track.color}15,${track.color}08)`,
            padding:     '16px 18px',
            display:     'flex',
            alignItems:  'center',
            gap:         12,
            borderBottom:'1px solid #EEEFF1',
          }}>
            <div style={{
              width:          44,
              height:         44,
              borderRadius:   10,
              background:     `linear-gradient(135deg,${track.color},${track.color}cc)`,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              fontSize:       22,
              flexShrink:     0,
            }}>{track.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:9, color:'#A2A6AA', letterSpacing:'0.1em', fontWeight:600, marginBottom:2 }}>{track.subtitle?.toUpperCase()}</div>
              <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, fontWeight:600, color:'#151715' }}>{track.title}</div>
              {track.description && <div style={{ fontSize:11, color:'#5C6065', marginTop:2 }}>{track.description}</div>}
            </div>
            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6, flexShrink:0 }}>
              <PublishToggle value={track.published} onChange={() => togglePublish(track)} />
              {track.published ? (
                <button type="button"
                  onClick={() => unpublishAllCascade(track)}
                  disabled={cascadingTrackId === track.id}
                  style={{ ...BTN_OUTLINE, padding:'5px 12px', fontSize:11, color:'#E24B4A', borderColor:'rgba(226,75,74,0.4)' }}>
                  {cascadingTrackId === track.id ? '⏳ Working...' : '📴 Unpublish All'}
                </button>
              ) : (
                <button type="button"
                  onClick={() => publishAllCascade(track)}
                  disabled={cascadingTrackId === track.id}
                  style={BTN_PUBLISH_ALL}>
                  {cascadingTrackId === track.id ? '⏳ Working...' : '🚀 Publish All'}
                </button>
              )}
              <div style={{ display:'flex', gap:6 }}>
                <button type="button"
                  onClick={() => setExpandedTrack(expandedTrack === track.id ? null : track.id)}
                  style={{ ...BTN_PRIMARY, padding:'5px 12px', fontSize:11 }}>
                  {expandedTrack === track.id ? '▲ Close' : '▼ Modules'}
                </button>
                <button type="button" onClick={() => startEdit(track)} style={{ ...BTN_OUTLINE, padding:'5px 10px', fontSize:11 }}>Edit</button>
                <button type="button" onClick={() => deleteTrack(track.id)} style={{ ...BTN_DANGER, padding:'5px 10px', fontSize:11 }}>Delete</button>
              </div>
            </div>
          </div>

          {/* Expanded modules + final exam */}
          {expandedTrack === track.id && (
            <div style={{ padding:'16px 18px' }}>
              <ModuleEditor trackId={track.id} />

              {/* 🏆 GOLD DIVIDER + FINAL EXAM SECTION */}
              <div style={{ position:'relative', margin:'24px 0 16px' }}>
                <div style={{ height:2, background:'linear-gradient(90deg,transparent,#4D4E8D,transparent)', borderRadius:2 }} />
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#fff', padding:'0 16px', fontSize:11, fontWeight:700, color:'#41427a', letterSpacing:'0.1em' }}>
                  🏆 FINAL EXAM 🏆
                </div>
              </div>

              {/* Final Exam Toggle Button */}
              <button type="button"
                onClick={() => setExpandedFinalExam(expandedFinalExam === track.id ? null : track.id)}
                style={{
                  width:'100%',
                  padding:'12px 16px',
                  background: expandedFinalExam === track.id
                    ? 'linear-gradient(135deg,#F5F6F7,#EEF0F8)'
                    : 'linear-gradient(135deg,#4D4E8D,#41427a)',
                  border: expandedFinalExam === track.id ? '1.5px solid #4D4E8D' : 'none',
                  borderRadius: 10,
                  cursor:'pointer',
                  fontSize:13,
                  fontWeight:600,
                  color: expandedFinalExam === track.id ? '#151715' : '#4D4E8D',
                  fontFamily:'inherit',
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'space-between',
                  marginBottom: expandedFinalExam === track.id ? 14 : 0,
                  flexWrap:'wrap',
                  gap:8,
                }}>
                <span>
                  {expandedFinalExam === track.id ? '▲ Close Final Exam Editor' : '+ Manage Final Exam (Sections & Questions)'}
                </span>
                <span style={{ fontSize:11, opacity:0.7 }}>
                  Track-level test · 80% to pass · Unlimited retakes
                </span>
              </button>

              {/* Final Exam Editor (expanded) */}
              {expandedFinalExam === track.id && (
                <div style={{ background:'#F7F8F9', padding:'14px', borderRadius:10, border:'1px solid #E2E4E7' }}>
                  {/* Show custom title preview */}
                  {(track.final_exam_title || track.final_exam_intro) && (
                    <div style={{ background:'linear-gradient(135deg,#4D4E8D,#41427a)', borderRadius:8, padding:'14px', marginBottom:14, textAlign:'center', border:'2px solid #4D4E8D' }}>
                      {track.final_exam_title && (
                        <div style={{ fontFamily:'Plus Jakarta Sans,sans-serif', fontSize:18, fontWeight:700, color:'#4D4E8D', marginBottom:4 }}>
                          🏆 {track.final_exam_title}
                        </div>
                      )}
                      {track.final_exam_intro && (
                        <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', fontStyle:'italic' }}>
                          {track.final_exam_intro}
                        </div>
                      )}
                      <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', marginTop:6 }}>
                        ↑ This is how notaries will see the exam heading. Edit in Track settings.
                      </div>
                    </div>
                  )}
                  <FinalExamEditor trackId={track.id} />
                </div>
              )}

              {/* 📥 RECOMMENDED RESOURCES SECTION */}
              <div style={{ position:'relative', margin:'24px 0 16px' }}>
                <div style={{ height:2, background:'linear-gradient(90deg,transparent,#41427a,transparent)', borderRadius:2 }} />
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', background:'#fff', padding:'0 16px', fontSize:11, fontWeight:700, color:'#41427a', letterSpacing:'0.1em' }}>
                  📥 RESOURCES 📥
                </div>
              </div>

              {/* Resources Toggle Button */}
              <button type="button"
                onClick={() => setExpandedResources(expandedResources === track.id ? null : track.id)}
                style={{
                  width:'100%',
                  padding:'12px 16px',
                  background: expandedResources === track.id
                    ? 'linear-gradient(135deg,#F5F6F7,#EEF0F8)'
                    : 'linear-gradient(135deg,#41427a,#4D4E8D)',
                  border: expandedResources === track.id ? '1.5px solid #4D4E8D' : 'none',
                  borderRadius: 10,
                  cursor:'pointer',
                  fontSize:13,
                  fontWeight:600,
                  color: expandedResources === track.id ? '#151715' : '#fff',
                  fontFamily:'inherit',
                  display:'flex',
                  alignItems:'center',
                  justifyContent:'space-between',
                  marginBottom: expandedResources === track.id ? 14 : 0,
                  flexWrap:'wrap',
                  gap:8,
                }}>
                <span>
                  {expandedResources === track.id ? '▲ Close Recommended Resources' : '+ Manage Recommended Resources'}
                </span>
                <span style={{ fontSize:11, opacity:0.85 }}>
                  Attach helpful documents from your Resource library
                </span>
              </button>

              {/* Resources Editor (expanded) */}
              {expandedResources === track.id && (
                <div style={{ background:'#F7F8F9', padding:'14px', borderRadius:10, border:'1px solid #E2E4E7' }}>
                  <TrackResourcesEditor trackId={track.id} />
                </div>
              )}
            </div>
          )}
        </div>
          )}
        </SortableList>
      )}

      {/* ── FOOTER NOTE ── */}
      <div style={{ marginTop:16, padding:'12px 16px', background:'#F7F8F9', border:'1px solid #E2E4E7', borderRadius:10, fontSize:11, color:'#5C6065', textAlign:'center' }}>
        🎓 Content published here appears instantly in the NSV Notary Education — no deployment needed!!
      </div>

    </main>
  )
}
